import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { Order } from "../../interfaces/OrderObject";
import { resetAllState } from "./resetAllState";
import {
  createOrder,
  fetchOrdersByBusinessId,
  respondToOrder,
} from "../../services/thunks/OrderThunk";

interface OrdersState {
  /** Orders keyed by businessId — a user may browse several businesses. */
  ordersByBusiness: Record<string, Order[]>;
  /** Resolved userId → display name, for disclosing who responded (A8). */
  userNameCache: Record<string, string>;
  /** requestId of the newest fetchOrders call; older responses are dropped. */
  latestFetchRequestId: string | null;
  loadingMap: Record<string, string | null>;
  errorMap: Record<string, string | null>;
  successMap: Record<string, boolean>;
}

const initialState: OrdersState = {
  ordersByBusiness: {},
  userNameCache: {},
  latestFetchRequestId: null,
  loadingMap: {},
  errorMap: {},
  successMap: {},
};

/** Insert-or-replace an order in its business bucket (newest first). */
function upsertOrder(state: OrdersState, order: Order): void {
  const list = state.ordersByBusiness[order.businessId] ?? [];
  const idx = list.findIndex((o) => o.orderId === order.orderId);
  if (idx === -1) {
    state.ordersByBusiness[order.businessId] = [order, ...list];
  } else {
    list[idx] = order;
  }
}

/** Merge a respond result (which may be partial) into the cached order, wherever it lives. */
function mergeRespondedOrder(state: OrdersState, partial: Order): void {
  for (const businessId of Object.keys(state.ordersByBusiness)) {
    const list = state.ordersByBusiness[businessId];
    const idx = list.findIndex((o) => o.orderId === partial.orderId);
    if (idx !== -1) {
      list[idx] = {
        ...list[idx],
        status: partial.status,
        respondedBy: partial.respondedBy ?? list[idx].respondedBy,
        respondedByName: partial.respondedByName ?? list[idx].respondedByName,
        updatedAt: partial.updatedAt ?? list[idx].updatedAt,
      };
      return;
    }
  }
}

const ordersSlice = createSlice({
  name: "orders",
  initialState,
  reducers: {
    cacheUserName(state, action: PayloadAction<{ userId: string; name: string }>) {
      state.userNameCache[action.payload.userId] = action.payload.name;
    },
  },
  extraReducers: (builder) => {
    builder
      // ── fetch ──
      .addCase(fetchOrdersByBusinessId.pending, (state, action) => {
        state.latestFetchRequestId = action.meta.requestId;
        state.loadingMap.fetchOrders = "Loading orders...";
        state.errorMap.fetchOrders = null;
        state.successMap.fetchOrders = false;
      })
      .addCase(fetchOrdersByBusinessId.fulfilled, (state, action) => {
        // A slower, older request (e.g. an earlier date-range pick) must not
        // overwrite the result of the one the user is actually looking at.
        if (action.meta.requestId !== state.latestFetchRequestId) return;
        state.loadingMap.fetchOrders = null;
        state.successMap.fetchOrders = true;
        state.ordersByBusiness[action.meta.arg.businessId] = action.payload;
      })
      .addCase(fetchOrdersByBusinessId.rejected, (state, action) => {
        if (action.meta.requestId !== state.latestFetchRequestId) return;
        state.loadingMap.fetchOrders = null;
        state.errorMap.fetchOrders = action.payload as string;
      })
      // ── create ──
      .addCase(createOrder.pending, (state) => {
        state.loadingMap.createOrder = "Placing order...";
        state.errorMap.createOrder = null;
        state.successMap.createOrder = false;
      })
      .addCase(createOrder.fulfilled, (state, action) => {
        state.loadingMap.createOrder = null;
        state.successMap.createOrder = true;
        upsertOrder(state, action.payload);
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.loadingMap.createOrder = null;
        state.errorMap.createOrder = action.error.message ?? "Could not place order.";
      })
      // ── respond ──
      .addCase(respondToOrder.pending, (state) => {
        state.loadingMap.respondToOrder = "Updating order...";
        state.errorMap.respondToOrder = null;
        state.successMap.respondToOrder = false;
      })
      .addCase(respondToOrder.fulfilled, (state, action) => {
        state.loadingMap.respondToOrder = null;
        state.successMap.respondToOrder = true;
        mergeRespondedOrder(state, action.payload);
      })
      .addCase(respondToOrder.rejected, (state, action) => {
        state.loadingMap.respondToOrder = null;
        state.errorMap.respondToOrder = action.error.message ?? "Could not update order.";
      })
      .addCase(resetAllState, () => initialState);
  },
});

export const { cacheUserName } = ordersSlice.actions;

export const selectOrdersForBusiness =
  (businessId: string | null | undefined) =>
  (state: any): Order[] =>
    (businessId && state.orders.ordersByBusiness[businessId]) || [];

export const selectOrdersLoadingMap = (state: any) => state.orders.loadingMap;
export const selectOrdersErrorMap = (state: any) => state.orders.errorMap;
export const selectOrdersSuccessMap = (state: any) => state.orders.successMap;
export const selectUserNameCache = (state: any): Record<string, string> =>
  state.orders.userNameCache;

export default ordersSlice.reducer;
